import React from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, Phone, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import { CLINIC } from "@/lib/clinicInfo";

/**
 * IolGuide — patient-facing counselling document.
 * Rendered as a stack of A4-sized "pages" that print cleanly to PDF from the browser.
 * Digital viewers see a beautiful preview; patients can Print → Save as PDF.
 */
export default function IolGuide() {
  const handlePrint = () => window.print();
  return (
    <>
      <Seo
        title="Your Cataract & IOL Guide — Nehchal Eye Centre Noida"
        description="A personalised counselling guide from Dr. Gurkiran Kaur — understand your intraocular lens options and choose the right one for your life."
        path="/cataract/iol-guide"
      />

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .grain::before { display: none !important; }
          .iol-page { box-shadow: none !important; margin: 0 !important; border: none !important; page-break-after: always; }
          .iol-page:last-child { page-break-after: auto; }
        }
        @page { size: A4; margin: 0; }
        .iol-page {
          width: 210mm;
          min-height: 297mm;
          padding: 22mm 20mm;
          background: #fbf9f4;
          margin: 0 auto 24px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.08);
          position: relative;
          break-inside: avoid;
        }
        .iol-brand-strip {
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 6px;
          background: linear-gradient(90deg, #0f5c5d 0%, #c9964c 100%);
        }
        .iol-footer {
          position: absolute;
          bottom: 12mm; left: 20mm; right: 20mm;
          display: flex; justify-content: space-between; align-items: center;
          font-size: 10px; color: #6b7280;
          border-top: 1px solid #e5e0d5; padding-top: 8px;
        }
      `}</style>

      {/* Toolbar — hidden on print */}
      <div className="no-print bg-secondary/50 border-b border-border">
        <div className="container-wide py-4 flex flex-wrap items-center justify-between gap-3">
          <Link to="/cataract" className="inline-flex items-center gap-2 text-sm text-primary hover:opacity-80">
            <ArrowLeft className="h-4 w-4" /> Back to Cataract page
          </Link>
          <div className="flex items-center gap-3">
            <a href={`tel:${CLINIC.phoneRaw}`} className="hidden sm:inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:bg-secondary">
              <Phone className="h-4 w-4" /> {CLINIC.phone}
            </a>
            <button onClick={handlePrint} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm hover:bg-primary/90" data-testid="iol-print-btn">
              <Printer className="h-4 w-4" /> Download / Print PDF
            </button>
          </div>
        </div>
      </div>

      <div className="py-10 bg-secondary/30 print:bg-white">

        {/* COVER */}
        <article className="iol-page">
          <div className="iol-brand-strip" />
          <div className="pt-12">
            <img src={CLINIC.logo} alt="Nehchal Eye Centre" className="h-14 w-auto object-contain" />
            <div className="mt-16">
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9964c]">Patient Counselling Guide</div>
              <h1 className="font-serif text-5xl leading-[1.05] text-[#1a2332] mt-4">
                Your Cataract<br />&amp; IOL Guide.
              </h1>
              <p className="mt-6 text-[15px] leading-7 text-[#1a2332]/80 max-w-md">
                A personalised guide to understanding your intraocular lens options — prepared for you by <strong>Dr. Gurkiran Kaur</strong>.
              </p>
              <div className="mt-16 inline-flex items-center gap-2 rounded-full bg-[#0f5c5d]/10 text-[#0f5c5d] px-3.5 py-1.5 text-[10px] tracking-[0.2em] uppercase">
                <Sparkles className="h-3 w-3" /> Please bring this to your consultation
              </div>
            </div>
          </div>
          <div className="iol-footer">
            <span>Nehchal Eye Centre · Sector 32, Noida</span>
            <span>nehchaleye.com</span>
          </div>
        </article>

        {/* PAGE 1 — Your Vision After Cataract Surgery */}
        <article className="iol-page">
          <div className="iol-brand-strip" />
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9964c]">Page 01</div>
          <h2 className="font-serif text-4xl leading-tight text-[#1a2332] mt-3">
            Your vision <em className="text-[#0f5c5d]">after</em> cataract surgery.
          </h2>
          <div className="mt-8 space-y-5 text-[15px] leading-7 text-[#1a2332]/85 max-w-lg">
            <p className="font-serif text-xl text-[#0f5c5d] italic">
              Cataract surgery isn&apos;t simply about removing the cataract.
            </p>
            <p>The lens you choose can influence how you see the world afterwards — how you read your phone in bed, how you drive at night, whether you pick up your glasses fifty times a day.</p>
            <p>Two people can have the same surgery on the same day with the same surgeon, and walk out with two very different visual lives — because they chose two different intraocular lenses.</p>
            <p>This short guide explains your options, and helps us pick the right one <em>for you</em>.</p>
          </div>
          <div className="mt-10 rounded-lg border border-[#e5e0d5] bg-white p-5 max-w-lg">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#c9964c]">What we&apos;ll cover</div>
            <ol className="mt-3 text-sm text-[#1a2332]/80 space-y-1.5 list-decimal list-inside">
              <li>Which lens is right for you</li>
              <li>Understanding the trade-offs — there is no universally best lens</li>
              <li>Tell us about your life &mdash; your personalised questionnaire</li>
              <li>What to expect during recovery</li>
              <li>Your consultation checklist</li>
            </ol>
          </div>
          <div className="iol-footer">
            <span>Your Cataract &amp; IOL Guide</span>
            <span>01</span>
          </div>
        </article>

        {/* PAGE 2 — Which lens is right for you */}
        <article className="iol-page">
          <div className="iol-brand-strip" />
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9964c]">Page 02</div>
          <h2 className="font-serif text-4xl leading-tight text-[#1a2332] mt-3">
            Which lens is right for you?
          </h2>
          <div className="mt-8 rounded-lg overflow-hidden border border-[#e5e0d5]">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="bg-[#0f5c5d] text-white">
                  <th className="text-left px-4 py-3 font-serif font-medium">IOL Type</th>
                  <th className="text-left px-4 py-3 font-serif font-medium">Best Suited For</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {[
                  { l: "Monofocal", r: "Clear distance vision — glasses usually needed for near" },
                  { l: "Toric", r: "Cataract with significant astigmatism" },
                  { l: "EDOF", r: "Greater range of vision with reduced spectacle dependence" },
                  { l: "Multifocal / Trifocal", r: "Greater spectacle independence in selected patients" },
                ].map((row, i) => (
                  <tr key={row.l} className={i % 2 ? "bg-[#faf8f3]" : ""}>
                    <td className="px-4 py-4 font-serif text-[#0f5c5d] font-medium">{row.l}</td>
                    <td className="px-4 py-4 text-[#1a2332]/85">{row.r}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-10 rounded-lg border-l-4 border-[#c9964c] bg-white p-6">
            <div className="font-serif text-2xl italic text-[#0f5c5d]">
              &ldquo;There is no universally best lens.&rdquo;
            </div>
            <p className="mt-3 text-[14px] leading-7 text-[#1a2332]/80">
              The best lens is the one that fits <strong>your eyes, your lifestyle and your expectations</strong> — not the most expensive one, and not the one your friend chose.
            </p>
          </div>

          <p className="mt-6 text-[13px] leading-6 text-[#1a2332]/70 max-w-lg">
            Every IOL comes with trade-offs. Multifocal lenses may give you glasses freedom but slightly reduce contrast at night. Monofocal lenses give the sharpest single-distance vision but need readers. We will walk through this in detail during your consultation.
          </p>
          <div className="iol-footer">
            <span>Your Cataract &amp; IOL Guide</span>
            <span>02</span>
          </div>
        </article>

        {/* PAGE 3 — Tell us about your life */}
        <article className="iol-page">
          <div className="iol-brand-strip" />
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9964c]">Page 03</div>
          <h2 className="font-serif text-4xl leading-tight text-[#1a2332] mt-3">
            Tell us about your life.
          </h2>
          <p className="mt-4 text-[14px] text-[#1a2332]/75 max-w-lg">
            Please tick the boxes that apply to you. Your answers help us recommend the right lens — because IOL selection is personalised medicine.
          </p>

          <div className="mt-8 space-y-3 max-w-xl">
            {[
              "Do you drive at night?",
              "Do you read frequently — books, newspapers, phone?",
              "Do you use a computer or screen for more than 3 hours a day?",
              "Do you play golf, tennis or any outdoor sport?",
              "Do you travel frequently?",
              "Are you comfortable continuing to wear reading glasses?",
              "Do you prioritise spectacle independence — even if it means a small trade-off in night vision?",
            ].map((q, i) => (
              <div key={q} className="flex items-start gap-4 rounded-lg border border-[#e5e0d5] bg-white p-4">
                <div className="text-[#c9964c] font-serif text-lg font-medium min-w-[24px]">{String(i + 1).padStart(2, "0")}</div>
                <div className="flex-1 text-[14px] text-[#1a2332] leading-6">{q}</div>
                <div className="flex gap-2 items-center text-[11px] text-[#1a2332]/70">
                  <span className="inline-flex items-center gap-1"><span className="inline-block h-4 w-4 border-[1.5px] border-[#0f5c5d] rounded-sm" /> Yes</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-block h-4 w-4 border-[1.5px] border-[#0f5c5d] rounded-sm" /> No</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-lg bg-[#0f5c5d]/8 border border-[#0f5c5d]/20 p-5 max-w-xl">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#0f5c5d]">Anything else?</div>
            <div className="mt-2 h-16 border-b border-dashed border-[#0f5c5d]/30" />
          </div>
          <div className="iol-footer">
            <span>Your Cataract &amp; IOL Guide</span>
            <span>03</span>
          </div>
        </article>

        {/* PAGE 4 — Recovery */}
        <article className="iol-page">
          <div className="iol-brand-strip" />
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9964c]">Page 04</div>
          <h2 className="font-serif text-4xl leading-tight text-[#1a2332] mt-3">
            What to expect during recovery.
          </h2>
          <div className="mt-8 space-y-4 max-w-lg">
            {[
              { d: "Day 1", n: "Follow-up visit at the clinic. Vision usually functional. Begin eye drops as prescribed." },
              { d: "Week 1", n: "Most patients read, watch TV and do light housework. Avoid dust, swimming and eye rubbing." },
              { d: "Week 4", n: "Final spectacle prescription if needed. Return to full activity including exercise and driving at night." },
              { d: "Month 3", n: "Final settled vision. Second eye can now be planned if applicable." },
            ].map((r) => (
              <div key={r.d} className="flex gap-4 rounded-lg border border-[#e5e0d5] bg-white p-4">
                <div className="min-w-[80px]">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#c9964c]">Timeline</div>
                  <div className="font-serif text-lg text-[#0f5c5d] mt-1">{r.d}</div>
                </div>
                <p className="text-[14px] leading-6 text-[#1a2332]/85 flex-1">{r.n}</p>
              </div>
            ))}
          </div>
          <div className="iol-footer">
            <span>Your Cataract &amp; IOL Guide</span>
            <span>04</span>
          </div>
        </article>

        {/* PAGE 5 — Consultation Checklist */}
        <article className="iol-page">
          <div className="iol-brand-strip" />
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9964c]">Page 05</div>
          <h2 className="font-serif text-4xl leading-tight text-[#1a2332] mt-3">
            Your consultation checklist.
          </h2>
          <p className="mt-4 text-[14px] text-[#1a2332]/75 max-w-lg">
            Please bring these to your cataract consultation so we can plan your IOL accurately.
          </p>
          <ul className="mt-8 space-y-3 max-w-lg">
            {[
              "This filled-in guide",
              "Your current glasses and any older prescriptions",
              "Complete list of medications you take (including eye drops)",
              "Any previous eye reports, OCT scans or biometry reports",
              "Your health-insurance card if you plan to use cashless facility",
              "A family member or friend for the ride home if we dilate your eyes",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-[14px] leading-6 text-[#1a2332]">
                <span className="inline-block h-4 w-4 border-[1.5px] border-[#0f5c5d] rounded-sm mt-1 shrink-0" />
                {item}
              </li>
            ))}
          </ul>

          <div className="mt-14 grid grid-cols-2 gap-6 max-w-xl">
            <div className="rounded-lg border border-[#e5e0d5] bg-white p-5">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#c9964c]">Book your consultation</div>
              <div className="font-serif text-lg text-[#0f5c5d] mt-2">{CLINIC.phone}</div>
              <div className="text-[12px] text-[#1a2332]/70 mt-1">Mon–Sat · 9 AM – 7 PM</div>
              <div className="text-[12px] text-[#1a2332]/70">Sun · 10 AM – 1 PM</div>
            </div>
            <div className="rounded-lg border border-[#e5e0d5] bg-white p-5">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#c9964c]">Visit</div>
              <div className="font-serif text-sm text-[#0f5c5d] mt-2 leading-5">
                {CLINIC.address.line1}<br />
                {CLINIC.address.line2}
              </div>
              <div className="text-[12px] text-[#1a2332]/70 mt-2">nehchaleye.com</div>
            </div>
          </div>

          <div className="mt-10 pt-5 border-t border-[#e5e0d5] max-w-lg">
            <p className="text-[11px] text-[#1a2332]/60 leading-5 italic">
              This guide is a counselling tool prepared by Dr. Gurkiran Kaur and does not replace a personal medical consultation. Every recommendation is finalised only after a comprehensive eye examination.
            </p>
          </div>

          <div className="iol-footer">
            <span>Your Cataract &amp; IOL Guide</span>
            <span>05</span>
          </div>
        </article>
      </div>
    </>
  );
}
